import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import { useUser } from "../context/UserContext";
import StatusOverviewCard from "../components/stats/StatusOverviewCard";
import {
  Button,
  DataTable,
  DataTableCell,
  DataTableHead,
  DataTableRow,
  DateInput,
  EmptyState,
  FormField,
  PageHeader,
  SectionPanel,
  Select,
  StatusBadge as UiStatusBadge,
  TableToolbar,
  TextInput,
} from "../components/ui";
import { fetchWorkorderStats } from "../services/statsService";
import {
  getApiErrorMessage,
  hasApiErrorCode,
} from "../utils/apiError";

export default function Werkbonnen() {
  const navigate = useNavigate();
  const { user } = useUser();

  const roleNames = useMemo(() => getRoleNames(user), [user]);
  const isMonteur = roleNames.includes("monteur");
  const canViewAllWerkbonnen = roleNames.some((role) =>
    ["projectleider", "admin", "platformadmin"].includes(role)
  );

  const [scopeFilter, setScopeFilter] = useState("mine");
  const [werkbonnen, setWerkbonnen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [dateFromFilter, setDateFromFilter] = useState("");
  const [dateToFilter, setDateToFilter] = useState("");
  const [technicianFilter, setTechnicianFilter] = useState("all");
  const [disciplineFilter, setDisciplineFilter] = useState("all");
  const [expertiseSearchTerm, setExpertiseSearchTerm] = useState("");
  const [workTypeFilter, setWorkTypeFilter] = useState("all");
  const [workorderViewMode, setWorkorderViewMode] = useState(() => loadWorkorderViewMode());
  const [statusFilter, setStatusFilter] = useState("all");
  const [blockedFilter, setBlockedFilter] = useState("all");
  const [rowActionId, setRowActionId] = useState("");
  const [activeWerkbonId, setActiveWerkbonId] = useState("");
  const [selectedStatusKey, setSelectedStatusKey] = useState("");
  const [workorderStats, setWorkorderStats] = useState({ total: 0, statuses: [] });
  const [workorderStatsLoading, setWorkorderStatsLoading] = useState(true);
  const [workorderStatsError, setWorkorderStatsError] = useState("");

  const [agenda, setAgenda] = useState([]);
  const [agendaLoading, setAgendaLoading] = useState(true);
  const [agendaError, setAgendaError] = useState("");
  const [planningUsers, setPlanningUsers] = useState([]);
  const [planningPermissions, setPlanningPermissions] = useState([]);
  const currentUserId = String(user?.id || user?.userId || user?._id || "").trim();

  const canUsePlannerActions = useMemo(() => {
    if (isMonteur) return false;

    const hasRoleAccess = roleNames.some((role) =>
      [
        "planner",
        "projectleider",
        "werkvoorbereider",
        "tenantadmin",
        "platformadmin",
        "admin",
      ].includes(role)
    );

    const hasPermissionAccess = planningPermissions.some((permission) =>
      [
        "workorders.plan",
        "workorders.edit_preparation",
        "workorder.edit",
        "tenant.admin",
      ].includes(permission)
    );

    return hasRoleAccess || hasPermissionAccess;
  }, [isMonteur, planningPermissions, roleNames]);

  useEffect(() => {
    if (isMonteur || !canViewAllWerkbonnen) {
      setScopeFilter("mine");
    } else {
      setScopeFilter((prev) => (prev === "all" || prev === "mine" ? prev : "all"));
    }
  }, [isMonteur, canViewAllWerkbonnen]);

  const loadWerkbonnen = useCallback(async (activeScope = scopeFilter) => {
    try {
      setLoading(true);
      setError("");

      const wantsAll = activeScope === "all" && canViewAllWerkbonnen;
      const endpoint = wantsAll ? "/werkbonnen" : "/werkbonnen/mine";
      const res = await api.get(endpoint);
      setWerkbonnen(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      if (activeScope === "all" && canViewAllWerkbonnen) {
        try {
          const fallbackRes = await api.get("/werkbonnen/mine");
          setScopeFilter("mine");
          setWerkbonnen(Array.isArray(fallbackRes.data) ? fallbackRes.data : []);
          return;
        } catch (fallbackErr) {
          setError(getApiErrorMessage(fallbackErr, "Fout bij ophalen werkbonnen"));
        }
      } else {
        setError(getApiErrorMessage(err, "Fout bij ophalen werkbonnen"));
      }
    } finally {
      setLoading(false);
    }
  }, [scopeFilter, canViewAllWerkbonnen]);

  useEffect(() => {
    loadWerkbonnen(scopeFilter);
  }, [scopeFilter, loadWerkbonnen]);

  useEffect(() => {
    saveWorkorderViewMode(workorderViewMode);
  }, [workorderViewMode]);

  useEffect(() => {
    let active = true;

    async function loadWorkorderStats() {
      try {
        setWorkorderStatsLoading(true);
        setWorkorderStatsError("");
        const params = { period: "month" };
        if (scopeFilter === "mine" && currentUserId) {
          params.assignedTo = currentUserId;
        }
        const next = await fetchWorkorderStats(params);
        if (!active) return;
        setWorkorderStats(next || { total: 0, statuses: [] });
      } catch (err) {
        if (!active) return;
        setWorkorderStatsError(getApiErrorMessage(err, "Werkbon statistieken laden mislukt"));
      } finally {
        if (active) setWorkorderStatsLoading(false);
      }
    }

    loadWorkorderStats();
    return () => {
      active = false;
    };
  }, [currentUserId, scopeFilter]);

  useEffect(() => {
    async function fetchAgenda() {
      try {
        setAgendaLoading(true);
        setAgendaError("");
        const res = await api.get("/agenda");
        setAgenda(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        setAgendaError(getApiErrorMessage(err, "Fout bij ophalen agenda"));
      } finally {
        setAgendaLoading(false);
      }
    }

    fetchAgenda();
  }, []);

  useEffect(() => {
    async function fetchPlanningUsers() {
      if (isMonteur) {
        setPlanningPermissions([]);
        setPlanningUsers([]);
        return;
      }
      try {
        const [usersRes, meRes] = await Promise.all([
          api.get("/users?role=Monteur"),
          api.get("/rbac/me"),
        ]);
        setPlanningUsers(asUsersArray(usersRes.data));
        const permissions = Array.isArray(meRes?.data?.permissions)
          ? meRes.data.permissions.map((item) => String(item || "").trim().toLowerCase())
          : [];
        setPlanningPermissions(permissions);
      } catch (err) {
        setPlanningPermissions([]);
        setPlanningUsers([]);
      }
    }

    fetchPlanningUsers();
  }, [isMonteur]);

  function openWerkbon(wb) {
    if (!wb?._id) return;
    setActiveWerkbonId(String(wb._id));
    const base = isMonteur ? "/monteur/werkbon" : "/werkbon";
    navigate(`${base}/${wb._id}`);
  }

  async function handleReplanWerkbon(wb) {
    if (!wb?._id) return;

    const currentDate = toDateInputValue(wb?.plannedDate);
    const nextDate = window.prompt(
      "Nieuwe startdatum (YYYY-MM-DD)",
      currentDate || ""
    );

    if (nextDate === null) {
      return;
    }

    const cleanDate = String(nextDate || "").trim();
    if (!cleanDate) {
      setError("Herplannen mislukt: startdatum is verplicht.");
      return;
    }

    try {
      setRowActionId(`replan-${wb._id}`);
      setError("");
      await api.put(`/werkbonnen/${wb._id}/planning`, {
        plannedDate: cleanDate,
        plannedStartTime: wb?.plannedStartTime || "",
        assignedTo: getAssignedToValue(wb) || null,
      });
      await loadWerkbonnen(scopeFilter);
    } catch (err) {
      if (hasApiErrorCode(err, "VALIDATION_ERROR")) {
        setError("Controleer de planning-invoer en probeer opnieuw.");
      } else {
        setError(getApiErrorMessage(err, "Herplannen mislukt"));
      }
    } finally {
      setRowActionId("");
    }
  }

  async function handleAssignTechnician(wb) {
    if (!wb?._id) return;
    if (!canUsePlannerActions) {
      setError("Je hebt geen rechten om monteurs toe te wijzen.");
      return;
    }
    if (!planningUsers.length) {
      setError("Geen monteurs beschikbaar om toe te wijzen.");
      return;
    }

    const rankedTechnicians = rankTechniciansForWorkorder(planningUsers, wb);
    const listLabel = rankedTechnicians
      .map(({ user, hints }, index) => {
        const userId = getEntityId(user);
        return `${index + 1}. ${user?.naam || user?.email || userId || "-"}${
          hints.length ? ` (${hints.join(", ")})` : ""
        }`;
      })
      .join("\n");
    const currentAssigned = String(getAssignedToValue(wb) || "").trim();
    const selectedInput = window.prompt(
      `Kies monteur (nummer of user-id):\n\n${listLabel}`,
      currentAssigned
    );

    if (selectedInput === null) return;
    const selectedRaw = String(selectedInput || "").trim();
    if (!selectedRaw) {
      setError("Monteur toewijzen mislukt: kies een monteur.");
      return;
    }

    let selectedUserId = selectedRaw;
    if (/^\d+$/.test(selectedRaw)) {
      const index = Number(selectedRaw) - 1;
      if (index >= 0 && index < rankedTechnicians.length) {
        selectedUserId = String(getEntityId(rankedTechnicians[index]?.user) || "");
      }
    }

    const selectedUser = rankedTechnicians
      .map((entry) => entry.user)
      .find(
      (user) => String(getEntityId(user) || "") === String(selectedUserId || "")
    );
    const selectedUserIdValue = String(getEntityId(selectedUser) || "").trim();
    if (!selectedUserIdValue) {
      setError("Monteur toewijzen mislukt: onbekende monteurkeuze.");
      return;
    }

    const currentDate = toDateInputValue(wb?.plannedDate);
    const selectedDateInput = window.prompt(
      "Geplande datum (YYYY-MM-DD) is verplicht voor inplannen:",
      currentDate || ""
    );

    if (selectedDateInput === null) return;
    const plannedDate = String(selectedDateInput || "").trim();
    if (!plannedDate) {
      setError("Monteur toewijzen mislukt: geplande datum is verplicht.");
      return;
    }

    try {
      setRowActionId(`assign-${wb._id}`);
      setError("");
      await api.put(`/werkbonnen/${wb._id}/planning`, {
        assignedTo: selectedUserIdValue,
        plannedDate,
        plannedStartTime: String(wb?.plannedStartTime || "").trim() || "08:00",
      });
      await loadWerkbonnen(scopeFilter);
    } catch (err) {
      if (hasApiErrorCode(err, "VALIDATION_ERROR")) {
        setError("Controleer monteur, datum en locatie; planning is nog niet compleet.");
      } else {
        setError(getApiErrorMessage(err, "Monteur toewijzen mislukt"));
      }
    } finally {
      setRowActionId("");
    }
  }

  async function handleDeleteWerkbon(wb) {
    if (!wb?._id) return;

    const confirmed = window.confirm(
      `Werkbon "${wb?.werkbonNummer || wb?.nummer || "onbekend"}" verwijderen?`
    );

    if (!confirmed) {
      return;
    }

    try {
      setRowActionId(`delete-${wb._id}`);
      setError("");
      await api.delete(`/werkbonnen/${wb._id}`);
      await loadWerkbonnen(scopeFilter);
    } catch (err) {
      setError(getApiErrorMessage(err, "Werkbon verwijderen mislukt"));
    } finally {
      setRowActionId("");
    }
  }

  async function handleReassignProject(wb) {
    if (!wb?._id) return;

    try {
      setRowActionId(`reassign-${wb._id}`);
      setError("");

      const projectInput = window.prompt(
        "Nieuw projectnummer of projectnaam",
        String(wb?.projectNummer || wb?.projectId?.projectNummer || wb?.projectId?.naam || "").trim()
      );

      if (projectInput === null) return;
      const needle = String(projectInput || "").trim().toLowerCase();
      if (!needle) {
        setError("Project wijzigen mislukt: projectnummer of projectnaam is verplicht.");
        return;
      }

      const projectRes = await api.get("/projects");
      const projects = Array.isArray(projectRes.data) ? projectRes.data : [];

      const normalizedCandidates = projects.map((project) => ({
        project,
        nummer: String(project?.projectNummer || project?.number || "").trim(),
        naam: String(project?.naam || project?.title || "").trim(),
      }));

      let match = normalizedCandidates.find(
        ({ nummer, naam }) => nummer.toLowerCase() === needle || naam.toLowerCase() === needle
      );

      if (!match) {
        const fuzzy = normalizedCandidates.filter(
          ({ nummer, naam }) =>
            nummer.toLowerCase().includes(needle) || naam.toLowerCase().includes(needle)
        );

        if (fuzzy.length === 1) {
          [match] = fuzzy;
        } else if (fuzzy.length > 1) {
          setError(`Meerdere projecten gevonden (${fuzzy.length}). Gebruik exact projectnummer of exacte naam.`);
          return;
        }
      }

      if (!match?.project?._id) {
        setError("Project niet gevonden binnen deze tenant.");
        return;
      }

      const confirmed = window.confirm(
        `Werkbon ${wb?.werkbonNummer || wb?._id} verplaatsen naar project "${match.naam || "-"}" (${match.nummer || "-"})?`
      );
      if (!confirmed) return;

      await api.put(`/werkbonnen/${wb._id}/project-link`, {
        targetProjectId: match.project._id,
        reason: "Handmatige correctie projectkoppeling via PL werkbonnenoverzicht",
      });

      await loadWerkbonnen(scopeFilter);
    } catch (err) {
      setError(getApiErrorMessage(err, "Project wijzigen mislukt"));
    } finally {
      setRowActionId("");
    }
  }

  const filteredWerkbonnen = werkbonnen.filter((wb) => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    const haystack = [
      wb?.werkbonNummer,
      wb?.nummer,
      getWerkbonOmschrijving(wb),
      getWerkbonLocatie(wb),
      wb?.projectNummer,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    if (normalizedSearch && !haystack.includes(normalizedSearch)) {
      return false;
    }

    if (technicianFilter !== "all") {
      const assignedTo = getAssignedToValue(wb);
      if (String(assignedTo || "") !== String(technicianFilter)) {
        return false;
      }
    }

    if (disciplineFilter !== "all") {
      const disciplineKey = getDisciplineKey(wb);
      if (disciplineKey !== disciplineFilter) {
        return false;
      }
    }

    const normalizedExpertiseSearch = String(expertiseSearchTerm || "").trim().toLowerCase();
    if (normalizedExpertiseSearch) {
      const expertiseLabel = getDisciplineLabel(wb).toLowerCase();
      if (!expertiseLabel.includes(normalizedExpertiseSearch)) {
        return false;
      }
    }

    if (!matchesWorkTypeFilter(wb, workTypeFilter)) {
      return false;
    }

    const activeStatusFilterKey = selectedStatusKey || statusFilter;
    if (activeStatusFilterKey !== "all" && activeStatusFilterKey) {
      const normalizedStatus = normalizeWorkorderStatusKey(wb?.status);
      if (normalizedStatus !== activeStatusFilterKey) {
        return false;
      }
    }

    if (!matchesBlockedFilter(wb, blockedFilter)) {
      return false;
    }

    const dateKey = getDateFilterKey(wb);
    if (dateFromFilter && dateKey && dateKey < dateFromFilter) {
      return false;
    }
    if (dateToFilter && dateKey && dateKey > dateToFilter) {
      return false;
    }
    if ((dateFromFilter || dateToFilter) && !dateKey) {
      return false;
    }

    return true;
  });

  const technicianOptions = useMemo(() => {
    const map = new Map();
    for (const wb of werkbonnen) {
      const value = String(getAssignedToValue(wb) || "").trim();
      if (!value) continue;
      const label = getAssignedUserLabel(wb);
      if (!map.has(value)) {
        map.set(value, label || value);
      }
    }

    return Array.from(map.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => String(a.label).localeCompare(String(b.label), "nl"));
  }, [werkbonnen]);

  const disciplineOptions = useMemo(() => {
    const map = new Map();
    for (const wb of werkbonnen) {
      const key = getDisciplineKey(wb);
      const label = getDisciplineLabel(wb);
      if (!key || !label) continue;
      if (!map.has(key)) map.set(key, label);
    }

    return Array.from(map.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => String(a.label).localeCompare(String(b.label), "nl"));
  }, [werkbonnen]);

  const statusCardItems = useMemo(
    () =>
      (Array.isArray(workorderStats?.statuses) ? workorderStats.statuses : []).map((status) => ({
        key: status.key,
        label: status.label,
        value: Number(status.count || 0),
        count: Number(status.count || 0),
        colorClass: STATUS_COLOR_MAP[status.key] || "bg-slate-300",
      })),
    [workorderStats]
  );

  const primaryNavPath = useMemo(() => {
    if (roleNames.includes("platformadmin")) return "/admin/dashboard";
    if (roleNames.includes("projectleider") || roleNames.includes("tenantadmin")) return "/pl";
    return "/dashboard";
  }, [roleNames]);

  const pageTitle = scopeFilter === "all" ? "Werkbonnenoverzicht" : "Mijn werkbonnen";

  return (
    <div className="space-y-4 p-4 pt-16 sm:pt-4">
      <PageHeader
        eyebrow="Operationeel"
        title={pageTitle}
        description="Werkbonnen, planning en agenda in een compact operationeel overzicht."
        meta={
          <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-xs text-slate-500">
            <button type="button" onClick={() => navigate(primaryNavPath)} className="hover:text-slate-700">
              Start
            </button>
            <span>/</span>
            <span className="font-medium text-slate-700">{pageTitle}</span>
          </nav>
        }
      />

      {!isMonteur && (
        <div className="flex gap-2">
          <Button
            tone={scopeFilter === "all" ? "primary" : "secondary"}
            onClick={() => setScopeFilter("all")}
            disabled={!canViewAllWerkbonnen}
          >
            Alle werkbonnen
          </Button>
          <Button tone={scopeFilter === "mine" ? "primary" : "secondary"} onClick={() => setScopeFilter("mine")}>
            Mijn werkbonnen
          </Button>
        </div>
      )}

      <StatusOverviewCard
        title={scopeFilter === "all" ? "Werkbonnen deze maand" : "Mijn werkbonnen deze maand"}
        subtitle="Statusverdeling in huidige selectie"
        statuses={statusCardItems}
        totalOverride={Number(workorderStats?.total || 0)}
        totalSuffix=" werkbonnen"
        onStatusClick={(statusKey) =>
          setSelectedStatusKey((prev) => (prev === statusKey ? "" : statusKey))
        }
        emptyLabel={
          workorderStatsLoading
            ? "Werkbon statistieken laden..."
            : "Geen werkbon statistieken beschikbaar."
        }
      />
      {workorderStatsError ? (
        <p className="text-sm text-red-600">{workorderStatsError}</p>
      ) : null}
      {selectedStatusKey ? (
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500">
            Actieve statusfilter: {selectedStatusKey}
          </span>
          <Button size="sm" tone="secondary" onClick={() => setSelectedStatusKey("")}>
            Wissen
          </Button>
        </div>
      ) : null}

      <SectionPanel title="Werkbonnen" description="Filters, acties en overzicht in compacte dossierweergave.">
        <TableToolbar>
          <div className="inline-flex border border-slate-300 bg-white">
            <button
              type="button"
              onClick={() => setWorkorderViewMode("table")}
              className={`h-9 px-3 text-xs font-medium ${
                workorderViewMode === "table"
                  ? "bg-slate-900 text-white"
                  : "text-slate-700 hover:bg-slate-50"
              }`}
            >
                Tabel
            </button>
            <button
              type="button"
              onClick={() => setWorkorderViewMode("cards")}
              className={`h-9 border-l border-slate-300 px-3 text-xs font-medium ${
                workorderViewMode === "cards"
                  ? "bg-slate-900 text-white"
                  : "text-slate-700 hover:bg-slate-50"
              }`}
            >
                Kaarten
            </button>
          </div>
          {!isMonteur ? (
            <Button tone="primary" onClick={() => navigate("/werkbon/nieuw")}>
                + Nieuwe werkbon
            </Button>
          ) : null}
          <div className="min-w-[260px] flex-1">
            <FormField label="Zoeken">
              <TextInput
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Zoek op nummer, opdracht, locatie of project"
              />
            </FormField>
          </div>
        </TableToolbar>
        <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-8">
          <FormField label="Vanaf datum">
            <DateInput
              type="date"
              value={dateFromFilter}
              onChange={(e) => setDateFromFilter(e.target.value)}
            />
          </FormField>
          <FormField label="Tot datum">
            <DateInput
              type="date"
              value={dateToFilter}
              onChange={(e) => setDateToFilter(e.target.value)}
            />
          </FormField>
          <FormField label="Monteur">
            <Select
              value={technicianFilter}
              onChange={(e) => setTechnicianFilter(e.target.value)}
            >
              <option value="all">Alle monteurs</option>
              {technicianOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </FormField>
          <FormField label="Expertise">
            <Select
              value={disciplineFilter}
              onChange={(e) => setDisciplineFilter(e.target.value)}
            >
              <option value="all">Alle expertises</option>
              {disciplineOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </FormField>
          <FormField label="Expertise zoekterm">
            <TextInput
              value={expertiseSearchTerm}
              onChange={(e) => setExpertiseSearchTerm(e.target.value)}
              placeholder="Bijv. BMI"
            />
          </FormField>
          <FormField label="Type">
            <Select
              value={workTypeFilter}
              onChange={(e) => setWorkTypeFilter(e.target.value)}
            >
              <option value="all">Alle types</option>
              <option value="offerte">Offerte</option>
              <option value="regie">Regie</option>
            </Select>
          </FormField>
          <FormField label="Status">
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">Alle statussen</option>
              {STATUS_FILTER_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </FormField>
          <div className="flex items-end gap-2">
            <FormField label="Geblokkeerd" className="w-full">
              <Select
                value={blockedFilter}
                onChange={(e) => setBlockedFilter(e.target.value)}
              >
                <option value="all">Alles</option>
                <option value="blocked">Alleen geblokkeerd</option>
                <option value="not_blocked">Niet geblokkeerd</option>
              </Select>
            </FormField>
            <Button
              size="sm"
              tone="secondary"
              onClick={() => {
                setDateFromFilter("");
                setDateToFilter("");
                setTechnicianFilter("all");
                setDisciplineFilter("all");
                setExpertiseSearchTerm("");
                setWorkTypeFilter("all");
                setStatusFilter("all");
                setBlockedFilter("all");
                setSearchTerm("");
                setSelectedStatusKey("");
              }}
            >
              Reset
            </Button>
          </div>
        </div>

        {loading && <p className="text-sm text-slate-500">Werkbonnen laden...</p>}
        {error && <p className="text-sm text-red-600">{error}</p>}
        {!loading && !error && filteredWerkbonnen.length === 0 && (
          <EmptyState
            title="Geen werkbonnen gevonden"
            description="Pas filters aan of maak een nieuwe werkbon aan."
          />
        )}

        {!loading && filteredWerkbonnen.length > 0 && (
          <>
            {workorderViewMode === "table" ? (
              <DataTable>
                <DataTableHead>
                  <DataTableRow>
                    <DataTableCell header>Werkbon</DataTableCell>
                    <DataTableCell header>Omschrijving</DataTableCell>
                    <DataTableCell header>Startdatum</DataTableCell>
                    <DataTableCell header>Status</DataTableCell>
                    <DataTableCell header>Opname</DataTableCell>
                    <DataTableCell header className="text-right">Acties</DataTableCell>
                  </DataTableRow>
                </DataTableHead>
                  <tbody>
                    {filteredWerkbonnen.map((wb) => (
                      <FragmentRow
                        key={wb._id}
                        werkbon={wb}
                        isMonteur={isMonteur}
                        activeWerkbonId={activeWerkbonId}
                        rowActionId={rowActionId}
                        onOpen={openWerkbon}
                        onReplan={handleReplanWerkbon}
                        onAssignTechnician={handleAssignTechnician}
                        onReassignProject={handleReassignProject}
                        onDelete={handleDeleteWerkbon}
                        canUsePlannerActions={canUsePlannerActions}
                      />
                    ))}
                  </tbody>
              </DataTable>
            ) : (
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                {filteredWerkbonnen.map((wb) => (
                  <WorkorderCard
                    key={wb._id}
                    werkbon={wb}
                    isMonteur={isMonteur}
                    activeWerkbonId={activeWerkbonId}
                    rowActionId={rowActionId}
                    onOpen={openWerkbon}
                    onReplan={handleReplanWerkbon}
                    onAssignTechnician={handleAssignTechnician}
                    onReassignProject={handleReassignProject}
                    onDelete={handleDeleteWerkbon}
                    canUsePlannerActions={canUsePlannerActions}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </SectionPanel>

      <SectionPanel title="Monteuragenda" description="Geplande items direct naast werkbonoverzicht.">
        {agendaLoading && <p className="text-sm text-slate-500">Agenda laden...</p>}
        {agendaError && <p className="text-sm text-red-600">{agendaError}</p>}

        {!agendaLoading && !agendaError && agenda.length === 0 && (
          <EmptyState title="Geen agenda-items gevonden" />
        )}

        {!agendaLoading && agenda.length > 0 && (
          <DataTable>
            <DataTableHead>
              <DataTableRow>
                <DataTableCell header>Datum</DataTableCell>
                <DataTableCell header>Begin</DataTableCell>
                <DataTableCell header>Titel</DataTableCell>
                <DataTableCell header>Status</DataTableCell>
              </DataTableRow>
            </DataTableHead>
            <tbody>
              {agenda.map((item) => (
                <AgendaRow
                  key={item._id}
                  item={item}
                  activeWerkbonId={activeWerkbonId}
                  onOpen={openWerkbon}
                />
              ))}
            </tbody>
          </DataTable>
        )}
      </SectionPanel>
    </div>
  );
}

const STATUS_COLOR_MAP = Object.freeze({
  concept: "bg-slate-400",
  gereed_voor_planning: "bg-amber-400",
  gepland: "bg-sky-500",
  in_uitvoering: "bg-indigo-500",
  afgerond: "bg-emerald-500",
  gesloten: "bg-teal-600",
  wacht_op_materiaal: "bg-orange-500",
  wacht_op_klant: "bg-yellow-500",
  verlopen: "bg-rose-500",
});

const STATUS_FILTER_OPTIONS = Object.freeze([
  { value: "concept", label: "Concept" },
  { value: "gereed_voor_planning", label: "Gereed voor planning" },
  { value: "gepland", label: "Gepland" },
  { value: "in_uitvoering", label: "In uitvoering" },
  { value: "tijdelijk_stilgelegd", label: "Tijdelijk stilgelegd" },
  { value: "afgerond", label: "Afgerond" },
  { value: "gesloten", label: "Gesloten" },
  { value: "wacht_op_materiaal", label: "Wacht op materiaal" },
  { value: "wacht_op_klant", label: "Wacht op klant" },
  { value: "verlopen", label: "Verlopen" },
]);

function normalizeWorkorderStatusKey(value) {
  const raw = String(value || "").trim().toLowerCase();
  const map = {
    nieuw: "concept",
    aangemaakt: "concept",
    gereed: "afgerond",
    akkoord_klant: "afgerond",
    gefactureerd: "gesloten",
  };
  return map[raw] || raw || "concept";
}

function FragmentRow({
  werkbon,
  isMonteur,
  activeWerkbonId,
  rowActionId,
  onOpen,
  onReplan,
  onAssignTechnician,
  onReassignProject,
  onDelete,
  canUsePlannerActions,
}) {
  const canDelete = !isMonteur && String(werkbon?.status || "").trim().toLowerCase() === "concept";
  const isTodayAndActive =
    String(werkbon?._id || "") === String(activeWerkbonId || "") && isTodayDate(werkbon?.plannedDate);
  const rowClass = isTodayAndActive
    ? "border-b border-green-700 bg-green-600 text-white"
    : "border-b";
  const detailRowClass = isTodayAndActive
    ? "border-b border-green-700 bg-green-700 text-green-50"
    : "border-b bg-gray-50";

  return (
    <>
      <tr className={rowClass}>
        <td className="py-2 pr-3">{werkbon?.werkbonNummer || werkbon?.nummer || "-"}</td>
        <td className="py-2 pr-3">{getWerkbonOmschrijving(werkbon)}</td>
        <td className="py-2 pr-3">{formatDatum(werkbon?.plannedDate)}</td>
        <td className="px-3 py-2">
          <StatusBadge status={werkbon?.status} />
        </td>
        <td className="px-3 py-2">
          <OpnameBadge gedaan={werkbon?.opnameGedaan === true} datum={werkbon?.laatsteOpnameAt} />
        </td>
        <td className="px-3 py-2 text-right">
          <div className="flex justify-end gap-2">
            <Button tone="success" size="sm" onClick={() => onOpen(werkbon)}>
              Wijzigen
            </Button>
            {!isMonteur && (
              <Button
                tone="success"
                size="sm"
                onClick={() => onAssignTechnician(werkbon)}
                disabled={!canUsePlannerActions || rowActionId === `assign-${werkbon._id}`}
              >
                Monteur kiezen
              </Button>
            )}
            {!isMonteur && (
              <Button
                tone="primary"
                size="sm"
                onClick={() => onReassignProject(werkbon)}
                disabled={rowActionId === `reassign-${werkbon._id}`}
              >
                Project wijzigen
              </Button>
            )}
            {!isMonteur && (
              <Button
                tone="primary"
                size="sm"
                onClick={() => onReplan(werkbon)}
                disabled={!canUsePlannerActions || rowActionId === `replan-${werkbon._id}`}
              >
                Herplannen
              </Button>
            )}
            {canDelete && (
              <Button
                tone="danger"
                size="sm"
                onClick={() => onDelete(werkbon)}
                disabled={rowActionId === `delete-${werkbon._id}`}
              >
                Verwijderen
              </Button>
            )}
          </div>
        </td>
      </tr>
      <tr className={detailRowClass}>
        <td colSpan={6} className="px-3 py-3">
          <div className="grid gap-3 md:grid-cols-4">
            <InfoBlock
              label="Locatie"
              value={getWerkbonLocatie(werkbon)}
              inverted={isTodayAndActive}
            />
            <InfoBlock
              label="Project"
              value={werkbon?.projectNummer || werkbon?.projectId?.naam || "-"}
              inverted={isTodayAndActive}
            />
            <InfoBlock
              label="Monteur"
              value={getAssignedUserLabel(werkbon)}
              inverted={isTodayAndActive}
            />
            <InfoBlock
              label="Aangemaakt"
              value={formatDatum(werkbon?.createdAt)}
              inverted={isTodayAndActive}
            />
          </div>
        </td>
      </tr>
    </>
  );
}

function WorkorderCard({
  werkbon,
  isMonteur,
  activeWerkbonId,
  rowActionId,
  onOpen,
  onReplan,
  onAssignTechnician,
  onReassignProject,
  onDelete,
  canUsePlannerActions,
}) {
  const canDelete = !isMonteur && String(werkbon?.status || "").trim().toLowerCase() === "concept";
  const isTodayAndActive =
    String(werkbon?._id || "") === String(activeWerkbonId || "") && isTodayDate(werkbon?.plannedDate);

  return (
    <article
      className={`rounded-lg border p-3 ${
        isTodayAndActive ? "border-green-700 bg-green-50" : "border-slate-200 bg-white"
      }`}
    >
      <div className="mb-2 flex items-start justify-between gap-2">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Werkbon</p>
          <p className="text-sm font-semibold text-slate-900">{werkbon?.werkbonNummer || werkbon?.nummer || "-"}</p>
        </div>
        <StatusBadge status={werkbon?.status} />
      </div>

      <p className="text-sm text-slate-800">{getWerkbonOmschrijving(werkbon)}</p>

      <div className="mt-3 grid gap-2 sm:grid-cols-2">
        <InfoBlock label="Startdatum" value={formatDatum(werkbon?.plannedDate)} />
        <InfoBlock label="Opname" value={werkbon?.opnameGedaan === true ? "Gedaan" : "Niet gedaan"} />
        <InfoBlock label="Locatie" value={getWerkbonLocatie(werkbon)} />
        <InfoBlock label="Project" value={werkbon?.projectNummer || werkbon?.projectId?.naam || "-"} />
        <InfoBlock label="Monteur" value={getAssignedUserLabel(werkbon)} />
        <InfoBlock label="Aangemaakt" value={formatDatum(werkbon?.createdAt)} />
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        <Button tone="success" size="sm" onClick={() => onOpen(werkbon)}>
          Wijzigen
        </Button>
        {!isMonteur && (
          <Button
            tone="success"
            size="sm"
            onClick={() => onAssignTechnician(werkbon)}
            disabled={!canUsePlannerActions || rowActionId === `assign-${werkbon._id}`}
          >
            Monteur kiezen
          </Button>
        )}
        {!isMonteur && (
          <Button
            tone="primary"
            size="sm"
            onClick={() => onReassignProject(werkbon)}
            disabled={rowActionId === `reassign-${werkbon._id}`}
          >
            Project wijzigen
          </Button>
        )}
        {!isMonteur && (
          <Button
            tone="primary"
            size="sm"
            onClick={() => onReplan(werkbon)}
            disabled={!canUsePlannerActions || rowActionId === `replan-${werkbon._id}`}
          >
            Herplannen
          </Button>
        )}
        {canDelete && (
          <Button
            tone="danger"
            size="sm"
            onClick={() => onDelete(werkbon)}
            disabled={rowActionId === `delete-${werkbon._id}`}
          >
            Verwijderen
          </Button>
        )}
      </div>
    </article>
  );
}

function AgendaRow({ item, activeWerkbonId, onOpen }) {
  const agendaWerkbonId = String(resolveAgendaWerkbonId(item));
  const isTodayAndActive =
    agendaWerkbonId &&
    agendaWerkbonId === String(activeWerkbonId || "") &&
    isTodayDate(item?.geplandeDatum || item?.plannedDate || item?.date);

  return (
    <tr
      onClick={() => onOpen(item)}
      className={`cursor-pointer border-b ${
        isTodayAndActive ? "border-green-700 bg-green-600 text-white" : "hover:bg-gray-50"
      }`}
    >
      <td className="py-2 pr-3">{formatDatum(item.geplandeDatum)}</td>
      <td className="py-2 pr-3">{item.beginTijd || "-"}</td>
      <td className="py-2 pr-3">{item.titel || "-"}</td>
      <td className="py-2 pr-3">
        <StatusBadge status={item.status} />
      </td>
    </tr>
  );
}

function InfoBlock({ label, value, inverted = false }) {
  const labelClass = inverted
    ? "text-xs font-semibold uppercase tracking-wide text-green-100"
    : "text-xs font-semibold uppercase tracking-wide text-gray-500";
  const valueClass = inverted ? "mt-1 text-sm text-white" : "mt-1 text-sm text-gray-700";

  return (
    <div>
      <div className={labelClass}>{label}</div>
      <p className={valueClass}>{value || "-"}</p>
    </div>
  );
}

function OpnameBadge({ gedaan, datum }) {
  if (!gedaan) {
    return <UiStatusBadge tone="neutral" label="Niet gedaan" />;
  }

  return (
    <UiStatusBadge tone="success" label="Gedaan" title={datum ? `Laatste opname: ${formatDatum(datum)}` : "Opname gedaan"} />
  );
}

function StatusBadge({ status }) {
  return <UiStatusBadge status={status} label={status || "-"} />;
}

function formatDatum(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("nl-NL");
}

function toDateInputValue(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
}

function isTodayDate(value) {
  if (!value) return false;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return false;
  const now = new Date();
  return (
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate()
  );
}

function resolveAgendaWerkbonId(item) {
  if (!item) return "";
  if (item?.werkbon?._id) return String(item.werkbon._id);
  if (item?.werkbonId) return String(item.werkbonId);
  if (item?._id) return String(item._id);
  return "";
}

function getWerkbonOmschrijving(werkbon) {
  return (
    werkbon?.omschrijving ||
    werkbon?.opdracht?.omschrijving ||
    werkbon?.scope?.samenvatting ||
    "-"
  );
}

function getWerkbonLocatie(werkbon) {
  return werkbon?.locationText || werkbon?.opdracht?.locatie || "-";
}

function getAssignedToValue(werkbon) {
  const assignedTo = werkbon?.assignedTo;
  if (!assignedTo) return "";
  if (typeof assignedTo === "string") return assignedTo;
  return String(assignedTo?._id || assignedTo?.id || "");
}

function getAssignedUserLabel(werkbon) {
  const assignedTo = werkbon?.assignedTo;
  if (!assignedTo) return "-";
  if (typeof assignedTo === "string") return assignedTo;
  return assignedTo?.naam || assignedTo?.name || assignedTo?.email || "-";
}

function asUsersArray(value) {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.users)) return value.users;
  if (Array.isArray(value?.results)) return value.results;
  return [];
}

function getEntityId(value) {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number") return String(value);
  if (typeof value === "object") {
    if (value._id && typeof value._id === "object" && value._id.$oid) return String(value._id.$oid);
    if (value._id) return String(value._id);
    if (value.id) return String(value.id);
  }
  return "";
}

function rankTechniciansForWorkorder(users, werkbon) {
  const targetScopeId = getScopeIdValue(werkbon);
  const targetDisciplineKey = normalizeSkillKey(getDisciplineLabel(werkbon));

  const ranked = asUsersArray(users).map((user) => {
    let score = 0;
    const hints = [];

    const scopeIds = getUserScopeIds(user);
    if (targetScopeId && scopeIds.has(targetScopeId)) {
      score += 50;
      hints.push("scope");
    }

    const skillKeys = getUserSkillKeys(user);
    if (targetDisciplineKey && skillKeys.has(targetDisciplineKey)) {
      score += 30;
      hints.push("skill");
    }

    if (user?.technicianProfile?.active !== false) {
      score += 5;
    }

    return { user, score, hints };
  });

  ranked.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    return String(a?.user?.naam || a?.user?.email || "").localeCompare(
      String(b?.user?.naam || b?.user?.email || ""),
      "nl-NL"
    );
  });

  return ranked;
}

function getScopeIdValue(werkbon) {
  if (!werkbon) return "";
  if (typeof werkbon.scopeId === "string") return String(werkbon.scopeId || "").trim();
  return String(werkbon?.scopeId?._id || "").trim();
}

function getUserScopeIds(user) {
  const ids = new Set();
  const homeBase = String(getEntityId(user?.homeBaseScopeId) || "").trim();
  if (homeBase) ids.add(homeBase);
  for (const scopeId of user?.allowedScopeIds || []) {
    const value = String(getEntityId(scopeId) || "").trim();
    if (value) ids.add(value);
  }
  return ids;
}

function getUserSkillKeys(user) {
  const skills = Array.isArray(user?.technicianProfile?.skills) ? user.technicianProfile.skills : [];
  return new Set(
    skills
      .map((value) => normalizeSkillKey(value))
      .filter(Boolean)
  );
}

function normalizeSkillKey(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");
}

function getDateFilterKey(werkbon) {
  const candidate = werkbon?.plannedDate || werkbon?.plannedStartAt || werkbon?.createdAt;
  if (!candidate) return "";
  const date = new Date(candidate);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
}

function matchesBlockedFilter(werkbon, filter) {
  if (filter === "all") return true;
  const normalizedStatus = String(werkbon?.status || "").trim().toLowerCase();
  const isBlocked = Boolean(werkbon?.blocked) || normalizedStatus === "tijdelijk_stilgelegd";
  if (filter === "blocked") return isBlocked;
  if (filter === "not_blocked") return !isBlocked;
  return true;
}

function matchesWorkTypeFilter(werkbon, filter) {
  if (filter === "all") return true;
  const candidates = [
    String(werkbon?.workorderType || ""),
    String(werkbon?.werkType || ""),
    String(werkbon?.type || ""),
  ]
    .map((value) => value.trim().toLowerCase())
    .filter(Boolean);

  if (!candidates.length) return false;
  if (filter === "offerte") {
    return candidates.some((value) => value.includes("offerte"));
  }
  if (filter === "regie") {
    return candidates.some((value) => value.includes("regie"));
  }
  return true;
}

function getDisciplineLabel(werkbon) {
  return (
    normalizeTextValue(werkbon?.disciplineLabel) ||
    normalizeTextValue(werkbon?.discipline) ||
    normalizeTextValue(werkbon?.vakdiscipline) ||
    normalizeTextValue(werkbon?.scope?.discipline) ||
    normalizeTextValue(werkbon?.scope?.disciplineLabel) ||
    normalizeTextValue(werkbon?.category) ||
    normalizeTextValue(werkbon?.categorie) ||
    ""
  );
}

function getDisciplineKey(werkbon) {
  const label = getDisciplineLabel(werkbon);
  if (!label) return "";
  return label.trim().toLowerCase();
}

function normalizeTextValue(value) {
  if (typeof value !== "string") return "";
  return value.trim();
}

function getRoleNames(user) {
  const roleSet = new Set();
  if (user?.rol) roleSet.add(String(user.rol).toLowerCase());
  if (user?.role) roleSet.add(String(user.role).toLowerCase());
  for (const role of user?.roles || []) {
    if (typeof role === "string") {
      roleSet.add(role.toLowerCase());
    } else if (role?.naam || role?.name) {
      roleSet.add(String(role.naam || role.name).toLowerCase());
    }
  }
  return Array.from(roleSet);
}

const WORKORDER_VIEW_MODE_STORAGE_KEY = "werkbonnen.viewMode.v1";

function loadWorkorderViewMode() {
  if (typeof window === "undefined") return "table";
  const value = String(window.localStorage.getItem(WORKORDER_VIEW_MODE_STORAGE_KEY) || "")
    .trim()
    .toLowerCase();
  return value === "cards" ? "cards" : "table";
}

function saveWorkorderViewMode(value) {
  if (typeof window === "undefined") return;
  const mode = value === "cards" ? "cards" : "table";
  window.localStorage.setItem(WORKORDER_VIEW_MODE_STORAGE_KEY, mode);
}
